/**
 * MentionTextarea — textarea with @mention autocomplete.
 *
 * When the user types `@`, a dropdown appears with matching users and contacts.
 * Selecting an entry inserts `@[Display Name](wsid)` into the text.
 * The coreBOS workflow triggers email on the @mention pattern.
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

interface Mentionable {
  id: string;
  name: string;
  type: "user" | "contact";
  email?: string;
}

interface MentionTextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  onSubmit?: () => void;
  disabled?: boolean;
}

/** Cache mentionables so we only fetch once per session */
let cachedMentionables: Mentionable[] | null = null;

async function loadMentionables(): Promise<Mentionable[]> {
  if (cachedMentionables) return cachedMentionables;

  const results: Mentionable[] = [];

  try {
    // Fetch users
    const usersRes = await axios.post("/api/direct/query", {
      query: "SELECT id, first_name, last_name, user_name, email1 FROM Users WHERE status = 'Active' LIMIT 50;",
    });
    if (usersRes.data.success) {
      for (const u of usersRes.data.result || []) {
        const name = `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.user_name;
        if (name) results.push({ id: u.id, name, type: "user", email: u.email1 });
      }
    }
  } catch { /* */ }

  try {
    // Fetch portal contacts
    const contactsRes = await axios.post("/api/direct/query", {
      query: "SELECT id, firstname, lastname, email FROM Contacts WHERE portal = '1' LIMIT 100;",
    });
    if (contactsRes.data.success) {
      for (const c of contactsRes.data.result || []) {
        const name = `${c.firstname || ""} ${c.lastname || ""}`.trim();
        if (name) results.push({ id: c.id, name, type: "contact", email: c.email });
      }
    }
  } catch { /* */ }

  cachedMentionables = results;
  return results;
}

export const MentionTextarea: React.FC<MentionTextareaProps> = ({
  value,
  onChange,
  placeholder,
  rows = 3,
  onSubmit,
  disabled,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [mentionables, setMentionables] = useState<Mentionable[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [filter, setFilter] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionStart, setMentionStart] = useState(-1);

  // Load mentionables on mount
  useEffect(() => {
    loadMentionables().then(setMentionables);
  }, []);

  const filtered = filter
    ? mentionables.filter((m) =>
        m.name.toLowerCase().includes(filter.toLowerCase()) ||
        (m.email || "").toLowerCase().includes(filter.toLowerCase())
      )
    : mentionables;

  const insertMention = useCallback((item: Mentionable) => {
    const textarea = textareaRef.current;
    if (!textarea || mentionStart < 0) return;

    const before = value.slice(0, mentionStart);
    const after = value.slice(textarea.selectionStart);
    const mention = `@[${item.name}](${item.id}) `;
    const newValue = before + mention + after;

    onChange(newValue);
    setShowDropdown(false);
    setFilter("");
    setMentionStart(-1);

    // Restore cursor position after React re-renders
    requestAnimationFrame(() => {
      const pos = before.length + mention.length;
      textarea.focus();
      textarea.setSelectionRange(pos, pos);
    });
  }, [value, onChange, mentionStart]);

  const handleInput = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    const pos = e.target.selectionStart;
    // Check if we're in a @mention context
    const textBefore = newValue.slice(0, pos);
    const atIndex = textBefore.lastIndexOf("@");

    if (atIndex >= 0) {
      // Make sure the @ is at start of word (preceded by space, newline, or start)
      const charBefore = atIndex > 0 ? textBefore[atIndex - 1] : " ";
      if (charBefore === " " || charBefore === "\n" || atIndex === 0) {
        const query = textBefore.slice(atIndex + 1);
        // Only show dropdown if query has no spaces (still typing the mention)
        if (!query.includes(" ") && query.length <= 30) {
          setMentionStart(atIndex);
          setFilter(query);
          setShowDropdown(true);
          setSelectedIndex(0);
          return;
        }
      }
    }

    setShowDropdown(false);
    setFilter("");
    setMentionStart(-1);
  }, [onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (showDropdown && filtered.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((i) => (i + 1) % filtered.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((i) => (i - 1 + filtered.length) % filtered.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(filtered[selectedIndex]);
        return;
      }
      if (e.key === "Escape") {
        setShowDropdown(false);
        return;
      }
    }

    // Cmd/Ctrl+Enter to submit
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && onSubmit) {
      e.preventDefault();
      onSubmit();
    }
  }, [showDropdown, filtered, selectedIndex, insertMention, onSubmit]);

  // Scroll selected item into view
  useEffect(() => {
    if (!dropdownRef.current) return;
    const items = dropdownRef.current.children;
    if (items[selectedIndex]) {
      (items[selectedIndex] as HTMLElement).scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  return (
    <div style={{ position: "relative" }}>
      <textarea
        ref={textareaRef}
        className="vault-comment-textarea"
        value={value}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        onBlur={() => {
          // Delay to allow click on dropdown
          setTimeout(() => setShowDropdown(false), 200);
        }}
      />

      {showDropdown && filtered.length > 0 && (
        <div
          ref={dropdownRef}
          style={{
            position: "absolute",
            bottom: "100%",
            left: 0,
            right: 0,
            maxHeight: 200,
            overflowY: "auto",
            background: "var(--vault-bg)",
            border: "1px solid var(--vault-grid-line-strong)",
            boxShadow: "0 -4px 12px rgba(0,0,0,0.08)",
            zIndex: 1000,
          }}
        >
          {filtered.map((item, idx) => (
            <div
              key={item.id}
              onMouseDown={(e) => { e.preventDefault(); insertMention(item); }}
              style={{
                padding: "6px 12px",
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: idx === selectedIndex ? "#f0f0f0" : "transparent",
                borderBottom: "1px solid var(--vault-grid-line)",
              }}
            >
              <span>
                <span className="vault-text-small" style={{ fontWeight: 500 }}>
                  {item.name}
                </span>
                {item.email && (
                  <span className="vault-text-muted" style={{ fontSize: "var(--vault-text-tiny)", marginLeft: 8 }}>
                    {item.email}
                  </span>
                )}
              </span>
              <span
                className="vault-label-tiny"
                style={{
                  color: item.type === "user" ? "var(--vault-zone-pending-border)" : "var(--vault-zone-comment-border)",
                }}
              >
                {item.type === "user" ? "STAFF" : "CONTATTO"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
